
import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { StandardButton } from "@/components/ui/standard-button";
import { useEnhancedToast } from "@/hooks/use-enhanced-toast";
import { supabase } from "@/integrations/supabase/client";
import { z } from "zod";

const contactFormSchema = z.object({
  name: z.string().trim().min(1, "Name is required").max(100, "Name must be less than 100 characters"),
  email: z.string().trim().email("Invalid email address").max(255, "Email must be less than 255 characters"),
  message: z.string().trim().min(1, "Message is required").max(2000, "Message must be less than 2000 characters")
});

interface ContactModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const ContactModal = ({ isOpen, onClose }: ContactModalProps) => {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { toast } = useEnhancedToast();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);

    try {
      // Validate form data
      const validatedData = contactFormSchema.parse(formData);

      // Call the edge function to handle the contact form submission
      const { data, error } = await supabase.functions.invoke('contact-form', {
        body: validatedData
      });

      if (error) throw error;

      toast({
        variant: "success",
        title: "Signal transmitted",
        description: "Your message has been received. We'll respond to your transmission soon.",
      });

      setFormData({ name: "", email: "", message: "" });
      onClose();
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        toast({
          title: "Validation error",
          description: error.errors[0].message,
          variant: "destructive",
        });
      } else {
        toast({
          title: "Transmission failed",
          description: error.message || "Unable to send your message. Please try again.",
          variant: "destructive",
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 text-slate-200 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-center">
            <div className="w-12 h-12 mx-auto mb-4 rounded-full border-2 border-dashed border-blue-400 flex items-center justify-center">
              <div className="w-6 h-6 rounded-full border-2 border-blue-400 animate-pulse" />
            </div>
            Initiate Contact
          </DialogTitle>
        </DialogHeader>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2">
              Name
            </label>
            <Input
              id="name"
              name="name"
              type="text"
              required
              value={formData.name}
              onChange={handleChange}
              className="bg-slate-700 border-slate-600 text-slate-200 focus:border-blue-400"
              placeholder="Your name"
            />
          </div>
          
          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2">
              Email
            </label>
            <Input
              id="email"
              name="email"
              type="email"
              required
              autoComplete="new-password"
              value={formData.email}
              onChange={handleChange}
              className="bg-slate-700 border-slate-600 text-slate-200 focus:border-blue-400"
              placeholder="your@email.com"
            />
          </div>
          
          <div>
            <label htmlFor="message" className="block text-sm font-medium text-slate-300 mb-2">
              Message
            </label>
            <Textarea
              id="message"
              name="message"
              required
              rows={4}
              value={formData.message}
              onChange={handleChange}
              className="bg-slate-700 border-slate-600 text-slate-200 focus:border-blue-400 resize-none"
              placeholder="Your message..."
            />
          </div>
          
          <div className="flex space-x-3 pt-4">
            <StandardButton
              type="button"
              onClick={onClose}
              variant="ghost"
              className="flex-1"
              disabled={isSubmitting}
            >
              Cancel
            </StandardButton>
            <StandardButton
              type="submit"
              variant="standard"
              className="flex-1"
              disabled={isSubmitting}
            >
              {isSubmitting ? "Transmitting..." : "Send Signal"}
            </StandardButton>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactModal;
